import React, { useState, useEffect } from 'react';
import api from '../services/api';

const formatRelative = (ts) => {
  if (!ts) return '-';
  const ms = Date.now() - new Date(ts).getTime();
  const min = Math.floor(ms / 60000);
  if (min < 1) return 'hace segundos';
  if (min < 60) return `hace ${min}m`;
  const h = Math.floor(min / 60);
  if (h < 24) return `hace ${h}h ${min % 60}m`;
  const d = Math.floor(h / 24);
  return `hace ${d}d ${h % 24}h`;
};

const formatAbsolute = (ts) => {
  if (!ts) return '-';
  return new Date(ts).toLocaleString('es-AR', { hour12: false });
};

const ConnectionLogs = ({ user }) => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [autoRefresh, setAutoRefresh] = useState(true);

  const fetchData = async () => {
    try {
      const res = await api.get('/api/connection-logs');
      setData(res.data || []);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.message || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    if (!autoRefresh) return;
    const id = setInterval(fetchData, 30000);
    return () => clearInterval(id);
  }, [autoRefresh]);

  if (user?.role !== 'admin') {
    return (
      <div style={{ padding: '2rem', textAlign: 'center', color: '#ef4444' }}>
        Acceso denegado. Esta página es solo para administradores.
      </div>
    );
  }

  return (
    <div style={{ padding: '1.5rem', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <div>
          <h2 style={{ margin: 0, color: '#1f2937' }}>📜 Logs de conexión por sitio</h2>
          <p style={{ margin: '0.25rem 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>
            Últimas 5 novedades (conexiones / desconexiones) de cada sitio. Actualización automática cada 30s.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
          <label style={{ display: 'flex', gap: '0.4rem', alignItems: 'center', fontSize: '0.85rem', color: '#374151', cursor: 'pointer' }}>
            <input type="checkbox" checked={autoRefresh} onChange={e => setAutoRefresh(e.target.checked)} />
            Auto-refresh
          </label>
          <button onClick={fetchData} style={{
            padding: '0.5rem 0.9rem', background: '#3b82f6', color: 'white', border: 'none',
            borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem'
          }}>
            🔄 Actualizar
          </button>
        </div>
      </div>

      {loading && <div style={{ color: '#6b7280' }}>Cargando…</div>}
      {error && <div style={{ color: '#ef4444', padding: '0.75rem', background: '#fee2e2', borderRadius: '8px' }}>{error}</div>}

      {!loading && !error && data.length === 0 && (
        <div style={{ padding: '2rem', textAlign: 'center', color: '#6b7280', background: 'white', borderRadius: '8px' }}>
          No hay sitios configurados.
        </div>
      )}

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))', gap: '1rem' }}>
        {data.map(({ site, events }) => (
          <div key={site._id} style={{
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e5e7eb',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
            overflow: 'hidden'
          }}>
            <div style={{
              padding: '0.85rem 1rem',
              background: '#f3f4f6',
              borderBottom: '1px solid #e5e7eb',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0, fontSize: '1rem', color: '#1f2937' }}>📍 {site.name}</h3>
              <span style={{ fontSize: '0.75rem', color: '#6b7280' }}>
                {events.length} {events.length === 1 ? 'evento' : 'eventos'}
              </span>
            </div>
            <div>
              {events.length === 0 ? (
                <div style={{ padding: '1.25rem', textAlign: 'center', color: '#9ca3af', fontSize: '0.85rem' }}>
                  Sin novedades registradas
                </div>
              ) : events.map((ev) => {
                const isConnect = ev.eventType === 'connected';
                return (
                  <div key={ev._id} style={{
                    padding: '0.65rem 1rem',
                    borderBottom: '1px solid #f3f4f6',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '0.6rem'
                  }}>
                    <div style={{
                      width: '10px',
                      height: '10px',
                      borderRadius: '50%',
                      background: isConnect ? '#10b981' : '#ef4444',
                      boxShadow: `0 0 8px ${isConnect ? 'rgba(16,185,129,0.5)' : 'rgba(239,68,68,0.5)'}`,
                      flexShrink: 0
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.9rem', color: '#1f2937', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {ev.deviceName}
                        <span style={{
                          marginLeft: '0.4rem',
                          fontSize: '0.7rem',
                          fontWeight: 500,
                          padding: '0.1rem 0.4rem',
                          borderRadius: '4px',
                          background: isConnect ? 'rgba(16,185,129,0.15)' : 'rgba(239,68,68,0.15)',
                          color: isConnect ? '#059669' : '#dc2626'
                        }}>
                          {isConnect ? 'CONECTÓ' : 'DESCONECTÓ'}
                        </span>
                      </div>
                      <div style={{ fontSize: '0.7rem', color: '#6b7280', marginTop: '0.1rem' }}>
                        {ev.deviceId} • {formatAbsolute(ev.timestamp)} ({formatRelative(ev.timestamp)})
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ConnectionLogs;
